import { ArrowRight, BookOpen, CalendarDays, CheckCircle2, GraduationCap, PlayCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const COURSE_CARDS = {
  es: [
    {
      title: 'Marca Personal para Creadores',
      description: 'Posiciona tu voz, estructura tu propuesta y convierte tu presencia digital en una marca coherente.',
      meta: 'Curso intensivo',
    },
    {
      title: 'Contenido que Convierte',
      description: 'Aprende a planificar piezas, grabar con intención y publicar contenido pensado para resultados reales.',
      meta: 'Workshop práctico',
    },
    {
      title: 'Estrategia Digital para Negocios',
      description: 'Una guía clara para organizar campañas, medir rendimiento y tomar mejores decisiones de marketing.',
      meta: 'Programa guiado',
    },
  ],
  en: [
    {
      title: 'Personal Branding for Creators',
      description: 'Clarify your voice, shape your offer, and turn your online presence into a brand people remember.',
      meta: 'Intensive course',
    },
    {
      title: 'Content That Converts',
      description: 'Plan stronger pieces, film with purpose, and publish content designed to drive real business outcomes.',
      meta: 'Hands-on workshop',
    },
    {
      title: 'Digital Strategy for Businesses',
      description: 'A practical system to organize campaigns, track performance, and make sharper marketing decisions.',
      meta: 'Guided program',
    },
  ],
};

const COURSE_BENEFITS = {
  es: [
    'Lecciones claras y accionables',
    'Materiales pensados para negocio y marca personal',
    'Enfoque práctico, no solo teoría',
    'Ruta para pasar de idea a ejecución',
  ],
  en: [
    'Clear, actionable lessons',
    'Material built for business and personal brand growth',
    'Practical execution instead of theory alone',
    'A path that helps you move from idea to launch',
  ],
};

const CoursesPage = () => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith('en');
  const locale = isEnglish ? 'en' : 'es';

  const copy = isEnglish
    ? {
        eyebrow: 'Courses',
        title: 'Learn the Systems Behind the Brand',
        intro:
          'This page brings your training offer back into the site with a dedicated space for programs, workshops, and signature learning experiences.',
        primaryCta: 'Book a call',
        secondaryCta: 'Go to contact',
        sectionTitle: 'Featured learning experiences',
        sectionBody:
          'Use this section to present your current course catalog, upcoming cohorts, or private training offers.',
        benefitsTitle: 'What visitors can expect',
        benefitsBody:
          'A strong courses page should make the offer feel structured, premium, and easy to understand at a glance.',
        scheduleTitle: 'Launch-ready structure',
        scheduleItems: [
          'Hero area with a clear promise and call to action',
          'Course cards for current or upcoming programs',
          'Benefits section that explains the learning outcome',
          'Direct path to contact or enrollment',
        ],
        finalTitle: 'Ready to publish the full version?',
        finalBody:
          'If you want, the next step can be replacing this recovery version with your exact course content, pricing, modules, and enrollment links.',
      }
    : {
        eyebrow: 'Cursos',
        title: 'Aprende los sistemas detrás de la marca',
        intro:
          'Esta página vuelve a poner tu oferta educativa dentro del sitio con un espacio dedicado para programas, workshops y experiencias de aprendizaje.',
        primaryCta: 'Agendar llamada',
        secondaryCta: 'Ir a contacto',
        sectionTitle: 'Experiencias destacadas',
        sectionBody:
          'Aquí puedes presentar tu catálogo actual de cursos, próximas cohortes o entrenamientos privados.',
        benefitsTitle: 'Lo que la gente puede esperar',
        benefitsBody:
          'Una buena página de cursos debe hacer que la oferta se sienta clara, estructurada y profesional desde el primer vistazo.',
        scheduleTitle: 'Estructura lista para lanzar',
        scheduleItems: [
          'Hero con promesa clara y llamada a la acción',
          'Tarjetas para cursos o programas activos',
          'Sección de beneficios y resultados esperados',
          'Camino directo a contacto o matrícula',
        ],
        finalTitle: '¿Quieres que la dejemos completa?',
        finalBody:
          'Si quieres, el próximo paso puede ser reemplazar esta versión de recuperación con tu contenido exacto, precios, módulos y enlaces de inscripción.',
      };

  const courses = COURSE_CARDS[locale];
  const benefits = COURSE_BENEFITS[locale];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0F1C2E]">
      <Navbar />

      <main className="pt-20">
        <section className="relative overflow-hidden bg-[#0F1C2E] text-white">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 15% 20%, rgba(125,70,140,0.28), transparent 32%), radial-gradient(circle at 85% 25%, rgba(78,191,191,0.2), transparent 28%), radial-gradient(circle at 50% 90%, rgba(242,120,92,0.16), transparent 26%)',
            }}
          />

          <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <div className="mb-5 flex items-center gap-3">
                  <div className="h-2.5 w-2.5 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
                  <span className="font-poppins text-xs font-semibold uppercase tracking-[0.35em] text-[#4EBFBF]">
                    {copy.eyebrow}
                  </span>
                </div>

                <h1 className="max-w-3xl font-poppins text-5xl font-black leading-tight text-white sm:text-6xl">
                  {copy.title}
                </h1>

                <p className="mt-6 max-w-2xl font-source-sans text-lg leading-8 text-white/72">
                  {copy.intro}
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/contacto"
                    className="inline-flex items-center gap-2 rounded-full bg-[#F2785C] px-5 py-3 font-poppins text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  >
                    {copy.primaryCta}
                    <ArrowRight size={15} />
                  </Link>
                  <Link
                    to="/contacto"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 font-poppins text-sm font-semibold text-white/90 transition-colors hover:border-[#4EBFBF]/45 hover:text-white"
                  >
                    {copy.secondaryCta}
                  </Link>
                </div>
              </div>

              <div className="rounded-[32px] border border-white/10 bg-white/5 p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#7D468C]/18 text-[#4EBFBF]">
                    <GraduationCap size={22} />
                  </div>
                  <div>
                    <p className="font-poppins text-lg font-bold text-white">{copy.benefitsTitle}</p>
                    <p className="font-source-sans text-sm text-white/60">{copy.benefitsBody}</p>
                  </div>
                </div>

                <div className="mt-8 space-y-4">
                  {benefits.map((item) => (
                    <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/8 bg-black/10 px-4 py-4">
                      <CheckCircle2 className="mt-0.5 shrink-0 text-[#4EBFBF]" size={18} />
                      <p className="font-source-sans text-sm leading-6 text-white/78">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#F7F8FB] px-4 py-16 dark:bg-[#101B2B] sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <h2 className="font-poppins text-3xl font-bold text-[#233559] dark:text-white">
                {copy.sectionTitle}
              </h2>
              <p className="mt-3 font-source-sans text-base leading-7 text-[#233559]/68 dark:text-white/62">
                {copy.sectionBody}
              </p>
            </div>

            <div className="mt-10 grid gap-6 lg:grid-cols-3">
              {courses.map((course, index) => (
                <article
                  key={course.title}
                  className="rounded-[28px] bg-white p-7 shadow-[0_20px_60px_rgba(15,28,46,0.08)] dark:bg-[#162338]"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 rounded-full bg-[#7D468C]/10 px-3 py-1 font-poppins text-xs font-semibold uppercase tracking-[0.2em] text-[#7D468C]">
                      <BookOpen size={13} />
                      {course.meta}
                    </span>
                    <span className="font-poppins text-sm font-bold text-[#233559]/35 dark:text-white/25">
                      0{index + 1}
                    </span>
                  </div>

                  <h3 className="mt-6 font-poppins text-2xl font-bold text-[#233559] dark:text-white">
                    {course.title}
                  </h3>

                  <p className="mt-4 font-source-sans text-base leading-7 text-[#233559]/68 dark:text-white/62">
                    {course.description}
                  </p>

                  <Link
                    to="/contacto"
                    className="mt-6 inline-flex items-center gap-2 font-poppins text-sm font-semibold text-[#F2785C] transition-[gap] hover:gap-3"
                  >
                    <PlayCircle size={15} />
                    {copy.primaryCta}
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white px-4 py-16 dark:bg-[#0F1C2E] sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="rounded-[32px] bg-[#233559] p-8 text-white shadow-[0_20px_60px_rgba(15,28,46,0.18)]">
              <div className="mb-5 flex items-center gap-3">
                <CalendarDays className="text-[#4EBFBF]" size={20} />
                <h2 className="font-poppins text-2xl font-bold">{copy.scheduleTitle}</h2>
              </div>

              <div className="space-y-4">
                {copy.scheduleItems.map((item) => (
                  <div key={item} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
                    <div className="mt-1 h-2.5 w-2.5 shrink-0 bg-[#F2785C]" style={{ transform: 'rotate(45deg)' }} />
                    <p className="font-source-sans text-sm leading-6 text-white/78">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col justify-center rounded-[32px] bg-[#F7F8FB] p-8 dark:bg-[#162338]">
              <h2 className="font-poppins text-3xl font-bold text-[#233559] dark:text-white">
                {copy.finalTitle}
              </h2>
              <p className="mt-4 max-w-2xl font-source-sans text-base leading-7 text-[#233559]/70 dark:text-white/64">
                {copy.finalBody}
              </p>

              <div className="mt-8">
                <Link
                  to="/contacto"
                  className="inline-flex items-center gap-2 rounded-full bg-[#7D468C] px-6 py-3 font-poppins text-sm font-semibold text-white transition-opacity hover:opacity-92"
                >
                  {copy.secondaryCta}
                  <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CoursesPage;
